import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Download,
  FileDown,
  Info,
  Loader2,
  PackageCheck,
  Printer,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Activity,
  Bell,
  BarChart3,
  Coins,
  Sparkles
} from 'lucide-react';
import Shell from '../dashboard/Shell';
import { useToast } from '../../components/ui/Toast';
import { apiFetch } from '../../utils/api';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import MovementLogTab from './inventory-tracker/MovementLogTab';
import ReorderRadarTab from './inventory-tracker/ReorderRadarTab';
import InsightsTab from './inventory-tracker/InsightsTab';
import ValuationTab from './inventory-tracker/ValuationTab';
import './inventory-tracker/inventory-tracker.css';
import './InventoryReportPage.css';

// Top-level view switcher. `report` renders the existing 3-mode
// report; the other four are new Phase G tabs backed by the
// merchant-inventory endpoints. Tracker tabs are gated on
// `inventory.tracker_pro` (the endpoints enforce it too).
const TRACKER_VIEWS = [
  { value: 'report', label: 'Report', icon: ClipboardList, gated: false },
  { value: 'movements', label: 'Movement log', icon: Activity, gated: true },
  { value: 'reorder', label: 'Reorder radar', icon: Bell, gated: true },
  { value: 'insights', label: 'Insights', icon: BarChart3, gated: true },
  { value: 'valuation', label: 'Valuation', icon: Coins, gated: true },
];

const STOCK_OPTIONS = [
  { value: 'all', label: 'All stock' },
  { value: 'in_stock', label: 'In stock' },
  { value: 'low_stock', label: 'Low stock' },
  { value: 'out_of_stock', label: 'Out of stock' }
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Published' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ARCHIVED', label: 'Archived' }
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Product name' },
  { value: 'category', label: 'Category' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'createdAt', label: 'Created date' }
];

const stockLabels = {
  in_stock: 'In stock',
  low_stock: 'Low stock',
  out_of_stock: 'Out of stock'
};

const expiryLabels = {
  ok: 'Healthy',
  expiring_soon: 'Expiring soon',
  expired: 'Expired',
  no_expiry: 'No expiry'
};

const reportModes = [
  { value: 'inventory', label: 'Inventory list' },
  { value: 'count', label: 'Stock count sheet' },
  { value: 'expiry', label: 'Expiry report' }
];

const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatMoney = (value) => `₦${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const formatPdfMoney = (value) => `NGN ${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-NG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};
const todayLabel = () => new Date().toLocaleDateString('en-NG', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});

const escapeCsv = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const escapeHtml = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return text.replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[character]));
};

const downloadFile = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const InventoryReportPage = () => {
  const toast = useToast();
  const { hasAccess: hasTrackerAccess, loading: trackerAccessLoading } = useFeatureAccess('inventory.tracker_pro');
  const [view, setView] = useState('report');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('inventory');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    stockStatus: 'all',
    sortBy: 'name',
    sortOrder: 'asc',
    includeVariants: true
  });
  const [search, setSearch] = useState('');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        status: filters.status,
        category: filters.category,
        stockStatus: filters.stockStatus,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        includeVariants: String(filters.includeVariants),
        includeBatches: String(mode === 'expiry')
      });
      const response = await apiFetch(`/merchants/products/inventory-report?${params.toString()}`);
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.error || 'Unable to load inventory report');
      }
      setReport(payload);
    } catch (error) {
      toast.error(error.message || 'Unable to load inventory report');
    } finally {
      setLoading(false);
    }
  }, [filters, mode, toast]);

  useEffect(() => {
    // Only fetch the classic report payload when the Report view is
    // active. Tracker tabs pull from their own endpoints, so we save a
    // round-trip when a merchant lands on a tracker view first.
    if (view === 'report') fetchReport();
  }, [fetchReport, view]);

  useEffect(() => {
    // If the merchant loses tracker access mid-session (rare, but
    // possible after a downgrade), snap them back to the Report tab
    // so they don't sit on a tab that would otherwise be hidden.
    if (!trackerAccessLoading && !hasTrackerAccess && view !== 'report') {
      setView('report');
    }
  }, [hasTrackerAccess, trackerAccessLoading, view]);

  const rows = useMemo(() => {
    const source = mode === 'expiry' ? (report?.batchRows || []) : (report?.rows || []);
    const needle = search.trim().toLowerCase();
    if (!needle) return source;
    return source.filter((row) => [
      row.name,
      row.variant,
      row.sku,
      row.category,
      row.status,
      row.batchReference,
      row.expiryStatus
    ].some((value) => String(value || '').toLowerCase().includes(needle)));
  }, [mode, report, search]);

  const categories = useMemo(() => {
    const values = new Set((report?.categories || report?.products || []).map((item) => (
      typeof item === 'string' ? item : item.category
    )).filter(Boolean));
    return ['all', ...Array.from(values).sort((a, b) => a.localeCompare(b))];
  }, [report]);

  const generatedAt = report?.generatedAt ? new Date(report.generatedAt).toLocaleString('en-NG') : todayLabel();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const summary = useMemo(() => {
    if (mode === 'expiry') {
      return [
        { icon: PackageCheck, label: 'Batch rows', value: formatNumber(rows.length) },
        { icon: CheckCircle2, label: 'Batch units', value: formatNumber(report?.totals?.batchUnits) },
        { icon: AlertTriangle, label: 'Expiring soon', value: formatNumber(report?.totals?.expiring_soon) },
        { icon: FileDown, label: 'Expired', value: formatNumber(report?.totals?.expired) }
      ];
    }
    return [
      { icon: PackageCheck, label: 'Rows', value: formatNumber(rows.length) },
      { icon: CheckCircle2, label: 'Units on hand', value: formatNumber(report?.totals?.totalUnits) },
      { icon: AlertTriangle, label: 'Low stock', value: formatNumber(report?.totals?.low_stock) },
      { icon: FileDown, label: 'Retail value', value: formatMoney(report?.totals?.totalRetailValue) }
    ];
  }, [mode, report, rows.length]);

  const exportCsv = () => {
    const columns = mode === 'count'
      ? ['Product', 'Variant', 'SKU', 'Category', 'Status', 'System Qty', 'Counted Qty', 'Difference', 'Notes']
      : mode === 'expiry'
        ? ['Product', 'Variant', 'SKU', 'Category', 'Batch Reference', 'Batch Status', 'Expiry Date', 'Expiry Status', 'Qty Remaining', 'Cost/Unit', 'Retail Value']
        : ['Product', 'Variant', 'SKU', 'Category', 'Status', 'Stock Status', 'Qty', 'Low Stock At', 'Price', 'Retail Value'];
    const body = rows.map((row) => {
      if (mode === 'count') {
        return [row.name, row.variant, row.sku, row.category, row.status, row.quantity, '', '', ''];
      }
      if (mode === 'expiry') {
        return [
          row.name,
          row.variant,
          row.sku,
          row.category,
          row.batchReference,
          row.batchStatus,
          formatDate(row.expiryDate),
          expiryLabels[row.expiryStatus] || row.expiryStatus,
          row.quantity,
          row.costPerUnit,
          row.retailValue
        ];
      }
      return [
        row.name,
        row.variant,
        row.sku,
        row.category,
        row.status,
        stockLabels[row.stockStatus] || row.stockStatus,
        row.quantity,
        row.lowStockThreshold,
        row.price,
        row.retailValue
      ];
    });
    const csv = [columns, ...body].map((line) => line.map(escapeCsv).join(',')).join('\n');
    downloadFile(csv, `revstar-inventory-report-${Date.now()}.csv`, 'text/csv;charset=utf-8');
    toast.success('Inventory CSV downloaded');
  };

  const generatePdf = () => {
    if (!rows.length) {
      toast.warning('No inventory rows to export');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 36;
    const brand = { green: '#0f766e', dark: '#111827', muted: '#6b7280', line: '#d1d5db' };
    const columns = mode === 'count'
      ? [
          { label: 'Product', width: 150 },
          { label: 'Variant', width: 105 },
          { label: 'SKU', width: 82 },
          { label: 'Category', width: 90 },
          { label: 'System', width: 52 },
          { label: 'Counted', width: 58 },
          { label: 'Diff', width: 45 },
          { label: 'Notes', width: 155 }
        ]
      : mode === 'expiry'
        ? [
            { label: 'Product', width: 140 },
            { label: 'Variant', width: 95 },
            { label: 'SKU', width: 70 },
            { label: 'Batch', width: 80 },
            { label: 'Expiry', width: 74 },
            { label: 'Status', width: 78 },
            { label: 'Qty', width: 42 },
            { label: 'Value', width: 82 }
          ]
      : [
          { label: 'Product', width: 155 },
          { label: 'Variant', width: 105 },
          { label: 'SKU', width: 82 },
          { label: 'Category', width: 90 },
          { label: 'Stock', width: 72 },
          { label: 'Qty', width: 45 },
          { label: 'Price', width: 75 },
          { label: 'Value', width: 88 }
        ];

    const drawHeader = () => {
      doc.setFillColor(15, 118, 110);
      doc.rect(0, 0, pageWidth, 62, 'F');
      doc.setTextColor('#ffffff');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text('Revstar', margin, 28);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Merchant inventory report', margin, 45);

      doc.setTextColor(brand.dark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text(mode === 'count' ? 'Stock Count Sheet' : mode === 'expiry' ? 'Expiry Report' : 'Inventory Report', margin, 92);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(brand.muted);
      doc.text(`${report?.store?.name || 'Store'} • Generated ${generatedAt}`, margin, 108);
      doc.text(mode === 'expiry'
        ? `Batches: ${rows.length} • Units: ${formatNumber(report?.totals?.batchUnits)} • Expiring soon: ${formatNumber(report?.totals?.expiring_soon)} • Expired: ${formatNumber(report?.totals?.expired)}`
        : `Rows: ${rows.length} • Units: ${formatNumber(report?.totals?.totalUnits)} • Retail value: ${formatPdfMoney(report?.totals?.totalRetailValue)}`,
      margin, 123);
    };

    const drawTableHeader = (y) => {
      doc.setFillColor(243, 244, 246);
      doc.setDrawColor(209, 213, 219);
      doc.rect(margin, y, pageWidth - margin * 2, 24, 'FD');
      doc.setTextColor(brand.dark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      let x = margin + 8;
      columns.forEach((column) => {
        doc.text(column.label, x, y + 16);
        x += column.width;
      });
    };

    let y = 142;
    drawHeader();
    drawTableHeader(y);
    y += 24;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    rows.forEach((row, index) => {
      if (y > pageHeight - 52) {
        doc.addPage();
        drawHeader();
        y = 142;
        drawTableHeader(y);
        y += 24;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
      }

      if (index % 2 === 0) {
        doc.setFillColor(249, 250, 251);
        doc.rect(margin, y, pageWidth - margin * 2, 26, 'F');
      }
      doc.setDrawColor(229, 231, 235);
      doc.line(margin, y + 26, pageWidth - margin, y + 26);
      doc.setTextColor(brand.dark);

      const data = mode === 'count'
        ? [
            row.name,
            row.variant || '-',
            row.sku || '-',
            row.category || '-',
            formatNumber(row.quantity),
            '',
            '',
            ''
          ]
        : mode === 'expiry'
          ? [
              row.name,
              row.variant || '-',
              row.sku || '-',
              row.batchReference || '-',
              formatDate(row.expiryDate),
              expiryLabels[row.expiryStatus] || row.expiryStatus || '-',
              formatNumber(row.quantity),
              formatPdfMoney(row.retailValue)
            ]
        : [
            row.name,
            row.variant || '-',
            row.sku || '-',
            row.category || '-',
            stockLabels[row.stockStatus] || row.stockStatus,
            formatNumber(row.quantity),
            formatPdfMoney(row.price),
            formatPdfMoney(row.retailValue)
          ];

      let x = margin + 8;
      data.forEach((value, columnIndex) => {
        const text = doc.splitTextToSize(String(value), columns[columnIndex].width - 8);
        doc.text(text.slice(0, 2), x, y + 11);
        x += columns[columnIndex].width;
      });
      y += 26;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(brand.muted);
      doc.text(`Powered by Revstar • Page ${i} of ${pageCount}`, margin, pageHeight - 22);
    }

    doc.save(`revstar-${mode === 'count' ? 'stock-count' : mode === 'expiry' ? 'expiry-report' : 'inventory'}-${Date.now()}.pdf`);
    toast.success('Inventory PDF generated');
  };

  const buildPrintDocument = () => {
    const title = mode === 'count' ? 'Stock Count Sheet' : mode === 'expiry' ? 'Expiry Report' : 'Inventory Report';
    const storeName = report?.store?.name || 'Store';
    const columns = mode === 'count'
      ? ['Product', 'Variant', 'SKU', 'Category', 'Status', 'System Qty', 'Counted', 'Diff', 'Notes']
      : mode === 'expiry'
        ? ['Product', 'Variant', 'SKU', 'Category', 'Batch Ref', 'Expiry', 'Expiry Status', 'Qty']
        : ['Product', 'Variant', 'SKU', 'Category', 'Status', 'Qty', 'Stock', 'Retail Value'];

    const rowsHtml = rows.map((row) => {
      const cells = mode === 'count'
        ? [
            `<td><strong>${escapeHtml(row.name)}</strong><small>${escapeHtml(row.productType)}</small></td>`,
            `<td>${escapeHtml(row.variant || '-')}</td>`,
            `<td>${escapeHtml(row.sku || '-')}</td>`,
            `<td>${escapeHtml(row.category || '-')}</td>`,
            `<td>${escapeHtml(row.status || '-')}</td>`,
            `<td>${escapeHtml(formatNumber(row.quantity))}</td>`,
            '<td class="worksheet-cell"><span></span></td>',
            '<td class="worksheet-cell narrow"><span></span></td>',
            '<td class="worksheet-cell notes"><span></span></td>'
          ]
        : mode === 'expiry'
          ? [
              `<td><strong>${escapeHtml(row.name)}</strong><small>${escapeHtml(row.productType)}</small></td>`,
              `<td>${escapeHtml(row.variant || '-')}</td>`,
              `<td>${escapeHtml(row.sku || '-')}</td>`,
              `<td>${escapeHtml(row.category || '-')}</td>`,
              `<td>${escapeHtml(row.batchReference || '-')}</td>`,
              `<td>${escapeHtml(formatDate(row.expiryDate))}</td>`,
              `<td>${escapeHtml(expiryLabels[row.expiryStatus] || row.expiryStatus || '-')}</td>`,
              `<td>${escapeHtml(formatNumber(row.quantity))}</td>`
            ]
        : [
            `<td><strong>${escapeHtml(row.name)}</strong><small>${escapeHtml(row.productType)}</small></td>`,
            `<td>${escapeHtml(row.variant || '-')}</td>`,
            `<td>${escapeHtml(row.sku || '-')}</td>`,
            `<td>${escapeHtml(row.category || '-')}</td>`,
            `<td>${escapeHtml(row.status || '-')}</td>`,
            `<td>${escapeHtml(formatNumber(row.quantity))}</td>`,
            `<td>${escapeHtml(stockLabels[row.stockStatus] || row.stockStatus || '-')}</td>`,
            `<td>${escapeHtml(formatMoney(row.retailValue))}</td>`
          ];
      return `<tr>${cells.join('')}</tr>`;
    }).join('');

    return `<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(title)} - ${escapeHtml(storeName)}</title>
          <style>
            @page { size: A4 landscape; margin: 12mm; }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              background: #ffffff;
              color: #111827;
              font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            }
            .print-header {
              display: flex;
              justify-content: space-between;
              gap: 24px;
              padding-bottom: 14px;
              border-bottom: 3px solid #0f766e;
            }
            .brand {
              color: #0f766e;
              font-size: 12px;
              font-weight: 800;
              letter-spacing: 0.08em;
              text-transform: uppercase;
            }
            h1 {
              margin: 6px 0 0;
              color: #111827;
              font-size: 24px;
              line-height: 1.1;
            }
            .meta {
              margin-top: 7px;
              color: #667085;
              font-size: 11px;
            }
            .print-badge {
              align-self: flex-start;
              padding: 7px 10px;
              border: 1px solid #bae6e1;
              border-radius: 999px;
              background: #f0fdfa;
              color: #0f766e;
              font-size: 11px;
              font-weight: 800;
              white-space: nowrap;
            }
            .summary {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              margin: 14px 0;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              overflow: hidden;
            }
            .summary div {
              padding: 9px 10px;
              border-right: 1px solid #e5e7eb;
            }
            .summary div:last-child { border-right: 0; }
            .summary span {
              display: block;
              color: #667085;
              font-size: 9px;
              font-weight: 800;
              letter-spacing: 0.04em;
              text-transform: uppercase;
            }
            .summary strong {
              display: block;
              margin-top: 3px;
              color: #111827;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
            }
            th {
              padding: 8px 8px;
              background: #f8fafc;
              color: #667085;
              font-size: 8px;
              font-weight: 800;
              letter-spacing: 0.04em;
              text-align: left;
              text-transform: uppercase;
            }
            td {
              padding: 8px;
              border-bottom: 1px solid #e5e7eb;
              color: #1f2937;
              vertical-align: top;
            }
            tr { page-break-inside: avoid; }
            td strong {
              display: block;
              color: #111827;
              font-size: 10px;
            }
            td small {
              display: block;
              margin-top: 2px;
              color: #98a2b3;
              font-size: 8px;
              text-transform: uppercase;
            }
            .worksheet-cell span {
              display: block;
              width: 100%;
              min-height: 15px;
              border-bottom: 1px dashed #94a3b8;
            }
            .worksheet-cell.narrow { width: 56px; }
            .worksheet-cell.notes { width: 180px; }
            .footer {
              margin-top: 14px;
              color: #667085;
              font-size: 9px;
              text-align: right;
            }
          </style>
        </head>
        <body>
          <header class="print-header">
            <div>
              <div class="brand">Revstar Inventory Controls</div>
              <h1>${escapeHtml(title)}</h1>
              <div class="meta">${escapeHtml(storeName)} &middot; Generated ${escapeHtml(generatedAt)}</div>
            </div>
            <div class="print-badge">${escapeHtml(formatNumber(rows.length))} rows</div>
          </header>

          <section class="summary">
            ${summary.map((item) => `<div><span>${escapeHtml(item.label)}</span><strong>${escapeHtml(item.value)}</strong></div>`).join('')}
          </section>

          <table>
            <thead>
              <tr>${columns.map((column) => `<th>${escapeHtml(column)}</th>`).join('')}</tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>

          <footer class="footer">Powered by Revstar</footer>
        </body>
      </html>`;
  };

  const printReport = () => {
    if (!rows.length) {
      toast.warning('No inventory rows to print');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) {
      toast.warning('Allow pop-ups to print this report');
      return;
    }

    printWindow.opener = null;
    printWindow.document.write(buildPrintDocument());
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const renderTopTabs = () => {
    // Hide tracker tabs entirely for merchants without access — a
    // silent gate reads better than showing four locked tabs a Free
    // merchant can't use. Report tab remains visible for everyone.
    const visibleViews = TRACKER_VIEWS.filter((v) => {
      if (!v.gated) return true;
      // While the plan check is still loading, show them (avoids a
      // brief flash where they disappear then reappear).
      if (trackerAccessLoading) return true;
      return hasTrackerAccess;
    });
    return (
      <div className="itk-tabs" role="tablist" aria-label="Inventory views">
        {visibleViews.map((v) => {
          const Icon = v.icon;
          return (
            <button
              key={v.value}
              type="button"
              role="tab"
              aria-selected={view === v.value}
              className={`itk-tab${view === v.value ? ' active' : ''}`}
              onClick={() => setView(v.value)}
            >
              <Icon size={14} />
              {v.label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderTrackerGate = (message) => (
    <div className="itk-upgrade">
      <Sparkles size={24} color="#0d7463" style={{ marginBottom: 8 }} />
      <strong>Unlock the Inventory Tracker</strong>
      <p>{message} Available on Pro and Premium plans.</p>
    </div>
  );

  const renderTrackerView = () => {
    if (trackerAccessLoading) {
      return <div className="itk-loading"><Loader2 size={18} className="spin" /> Checking your plan…</div>;
    }
    if (!hasTrackerAccess) {
      const copy = {
        movements: 'See every stock event across storefront, POS, WhatsApp, and manual orders in one feed.',
        reorder: 'Set reorder points and get supplier-grouped restock suggestions.',
        insights: 'Track waste, revenue tiers, and inventory turnover in one dashboard.',
        valuation: 'Get a monthly at-cost snapshot of your stock for the accountant.',
      }[view] || 'Get the full Inventory Tracker.';
      return renderTrackerGate(copy);
    }
    if (view === 'movements') return <MovementLogTab />;
    if (view === 'reorder') return <ReorderRadarTab />;
    if (view === 'insights') return <InsightsTab />;
    if (view === 'valuation') return <ValuationTab />;
    return null;
  };

  return (
    <Shell>
      <main className="inventory-report-page">
        {renderTopTabs()}
        {view !== 'report' ? renderTrackerView() : (
        <>
        <section className="inventory-report-hero">
          <div>
            <h1>Inventory reports</h1>
            <p>Print current stock, export a clean inventory list, or create a count sheet for physical stock checks.</p>
          </div>

          <div className="inventory-report-actions">
            <button type="button" className="inventory-report-btn ghost" onClick={fetchReport} disabled={loading}>
              {loading ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />}
              Refresh
            </button>
            <button type="button" className="inventory-report-btn ghost" onClick={printReport}>
              <Printer size={15} />
              Print
            </button>
            <button type="button" className="inventory-report-btn ghost" onClick={exportCsv}>
              <Download size={15} />
              CSV
            </button>
            <button type="button" className="inventory-report-btn primary" onClick={generatePdf}>
              <FileDown size={15} />
              PDF
            </button>
          </div>
        </section>

        <section className="inventory-report-mode" aria-label="Report type">
          {reportModes.map((item) => (
            <button
              key={item.value}
              type="button"
              className={mode === item.value ? 'active' : ''}
              onClick={() => setMode(item.value)}
            >
              {item.label}
            </button>
          ))}
        </section>

        {mode === 'count' && (
          <section className="inventory-report-note" aria-label="Stock count sheet guidance">
            <Info size={16} />
            <div>
              <strong>Built for physical stock counts</strong>
              <span>
                Counted, difference, and notes are blank worksheet columns for print/PDF.
                Use them while counting stock, then update inventory from the product or restock screens.
              </span>
            </div>
          </section>
        )}

        {mode === 'expiry' && (
          <section className="inventory-report-note" aria-label="Expiry report guidance">
            <Info size={16} />
            <div>
              <strong>Batch-based expiry report</strong>
              <span>
                This report uses received stock batches, not the old product-level expiry field.
                It helps identify expired and soon-to-expire stock before selling or reordering.
              </span>
            </div>
          </section>
        )}

        <section className="inventory-report-summary" aria-label="Inventory summary">
          {summary.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.label}>
                <Icon size={18} />
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </article>
            );
          })}
        </section>

        <section className={`inventory-report-filters ${filtersOpen ? 'open' : ''}`}>
          <button
            type="button"
            className="inventory-report-filter-title"
            onClick={() => setFiltersOpen((value) => !value)}
            aria-expanded={filtersOpen}
          >
            <span>
              <SlidersHorizontal size={16} />
              Report filters
            </span>
            <ChevronDown size={17} />
          </button>

          <div className="inventory-report-filter-body">
            <div className="inventory-report-filter-grid">
              <label>
                Search
                <div className="inventory-report-search">
                  <Search size={15} />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Product, SKU, category"
                  />
                </div>
              </label>

              {mode !== 'expiry' && (
                <label>
                  Stock status
                  <select value={filters.stockStatus} onChange={(event) => handleFilterChange('stockStatus', event.target.value)}>
                    {STOCK_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
              )}

              <label>
                Product status
                <select value={filters.status} onChange={(event) => handleFilterChange('status', event.target.value)}>
                  {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>

              <label>
                Category
                <select value={filters.category} onChange={(event) => handleFilterChange('category', event.target.value)}>
                  {categories.map((category) => (
                    <option key={category} value={category}>{category === 'all' ? 'All categories' : category}</option>
                  ))}
                </select>
              </label>

              <label>
                Sort by
                <select value={filters.sortBy} onChange={(event) => handleFilterChange('sortBy', event.target.value)}>
                  {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>

              <label>
                Direction
                <select value={filters.sortOrder} onChange={(event) => handleFilterChange('sortOrder', event.target.value)}>
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </label>
            </div>

            <label className="inventory-report-toggle">
              <input
                type="checkbox"
                checked={filters.includeVariants}
                onChange={(event) => handleFilterChange('includeVariants', event.target.checked)}
              />
              <span>Show variant rows when products have variations</span>
            </label>
          </div>
        </section>

        <section className="inventory-report-card">
          <div className="inventory-report-card-header">
            <div>
              <h2>{mode === 'count' ? 'Stock count preview' : 'Inventory preview'}</h2>
              <p>{report?.store?.name || 'Store'} · Generated {generatedAt}</p>
            </div>
            <span>{rows.length} rows</span>
          </div>

          {loading ? (
            <div className="inventory-report-empty">
              <Loader2 size={20} className="spin" />
              Loading inventory report...
            </div>
          ) : rows.length === 0 ? (
            <div className="inventory-report-empty">
              No products match these report filters.
            </div>
          ) : (
            <>
              <div className="inventory-report-table-wrap">
                <table className="inventory-report-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Variant</th>
                      <th>SKU</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Qty</th>
                      {mode === 'count' ? (
                        <>
                          <th>Counted</th>
                          <th>Diff</th>
                          <th>Notes</th>
                        </>
                      ) : mode === 'expiry' ? (
                        <>
                          <th>Batch ref</th>
                          <th>Expiry</th>
                          <th>Expiry status</th>
                        </>
                      ) : (
                        <>
                          <th>Stock</th>
                          <th>Value</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.batchId || `${row.productId}-${row.variantId || 'parent'}`}>
                        <td>
                          <strong>{row.name}</strong>
                          <small>{row.productType}</small>
                        </td>
                        <td>{row.variant || '-'}</td>
                        <td>{row.sku || '-'}</td>
                        <td>{row.category || '-'}</td>
                        <td>{row.status}</td>
                        <td>{formatNumber(row.quantity)}</td>
                        {mode === 'count' ? (
                          <>
                            <td className="worksheet-cell"><span aria-hidden="true" /></td>
                            <td className="worksheet-cell short"><span aria-hidden="true" /></td>
                            <td className="worksheet-cell notes"><span aria-hidden="true" /></td>
                          </>
                        ) : mode === 'expiry' ? (
                          <>
                            <td>{row.batchReference || '-'}</td>
                            <td>{formatDate(row.expiryDate)}</td>
                            <td><span className={`stock-pill expiry-${row.expiryStatus}`}>{expiryLabels[row.expiryStatus] || row.expiryStatus}</span></td>
                          </>
                        ) : (
                          <>
                            <td><span className={`stock-pill ${row.stockStatus}`}>{stockLabels[row.stockStatus] || row.stockStatus}</span></td>
                            <td>{formatMoney(row.retailValue)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="inventory-report-mobile-list">
                {rows.map((row) => (
                  <article key={`${row.batchId || `${row.productId}-${row.variantId || 'parent'}`}-mobile`}>
                    <div>
                      <strong>{row.name}</strong>
                      <span>{row.variant || row.category || 'Product'}</span>
                    </div>
                    <dl>
                      <div><dt>SKU</dt><dd>{row.sku || '-'}</dd></div>
                      <div><dt>Qty</dt><dd>{formatNumber(row.quantity)}</dd></div>
                      {mode === 'count' ? (
                        <>
                          <div><dt>Counted</dt><dd className="mobile-worksheet-line" /></div>
                          <div><dt>Diff</dt><dd className="mobile-worksheet-line" /></div>
                          <div className="mobile-worksheet-notes"><dt>Notes</dt><dd className="mobile-worksheet-line" /></div>
                        </>
                      ) : mode === 'expiry' ? (
                        <>
                          <div><dt>Batch</dt><dd>{row.batchReference || '-'}</dd></div>
                          <div><dt>Expiry</dt><dd>{formatDate(row.expiryDate)}</dd></div>
                          <div><dt>Status</dt><dd>{expiryLabels[row.expiryStatus] || row.expiryStatus}</dd></div>
                          <div><dt>Value</dt><dd>{formatMoney(row.retailValue)}</dd></div>
                        </>
                      ) : (
                        <>
                          <div><dt>Status</dt><dd>{stockLabels[row.stockStatus] || row.status}</dd></div>
                          <div><dt>Value</dt><dd>{formatMoney(row.retailValue)}</dd></div>
                        </>
                      )}
                    </dl>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>
        </>
        )}
      </main>
    </Shell>
  );
};

export default InventoryReportPage;
